import { Mutation } from "@apollo/client/react/components";
import { Editor } from "@govlaunch/editor";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import { convertFromRaw, EditorState } from "draft-js";
import React, { ReactElement } from "react";
import { useToggle } from "react-use";
import useIsMobile from "~/lib/hooks/useIsMobile";
import useIsPhone from "~/lib/hooks/useIsPhone";
import EditComment from "~/lib/mutations/EditComment";
import RemoveComment from "~/lib/mutations/RemoveComment";
import apostrophe from "~/lib/utils/apostrophe";
import notUndefinedOrNull from "~/lib/utils/notUndefinedOrNull";
import { IComment, ICommentTargetType, IUpvoteTargetType } from "~/types/types";
import { MobileScreen, MobileScreenBody, MobileScreenHeader } from "~/components/mobile";
import { Margin } from "~/components/spacings";
import ToggleUpvote from "~/components/story/ToggleUpvote";
import CommentActions from "~/components/comment/CommentActions";
import ReplyComment from "~/components/comment/ReplyComment";
import { SingleReplyContextConsumer } from "~/components/comment/SingleReplyContext";
import { WriteComment } from "~/components/comment/WriteComment";
import CommentsQuery from "~/lib/queries/CommentsQuery";
import {
  IEditCommentMutation,
  IEditCommentMutationVariables,
} from "~/lib/mutations/__generated__/EditComment.generated";
import {
  IRemoveCommentMutationVariables,
  IRemoveCommentMutation,
} from "~/lib/mutations/__generated__/RemoveComment.generated";
import { ICommentFieldsLegacyFragment } from "~/lib/queries/fragments/__generated__/CommentFields.generated";
import { withRouter } from "next/router";
import get from "lodash/fp/get";
import { useSelfie } from "~/components/auth/Auth";
import { WithRouterProps } from "next/dist/client/with-router";
import { FormattedMessage, useIntl } from "react-intl";
import IntlRelativeTime from "~/src/shared/components/IntlRelativeTime";
import { IntlDateTimeComment } from "~/src/shared/components/IntlDateTime";

interface ICommentProps {
  targetId: string;
  targetType: ICommentTargetType;
  comment: ICommentFieldsLegacyFragment;
  replyTo?: ICommentFieldsLegacyFragment;
  preview?: boolean;
}

function Comment({
  targetId,
  targetType,
  comment,
  replyTo,
  router,
  preview,
}: ICommentProps & WithRouterProps): ReactElement | null {
  const intl = useIntl();
  const selfie = useSelfie();
  const isPhone = useIsPhone();
  const isMobile = useIsMobile();
  const [isEditing, toggleEdit] = useToggle(false);
  const isPrint = get("query.media", router) === "print";

  if (!selfie || !comment.author) {
    return null;
  }

  return (
    <div
      css={{
        display: "flex",
        alignItems: "flex-start",
      }}
    >
      <ProfilePicture
        size={isMobile ? 32 : 38}
        image={comment.author.avatar}
        name={comment.author.fullName}
        innovator={comment.author.__typename === "GovernmentUser" && comment.author.isInnovator}
      />

      <div
        css={{
          marginLeft: 10,
          width: "100%",
        }}
      >
        <div>
          <a
            css={{
              color: palette.text,
              fontWeight: 600,
              fontSize: 14,
              lineHeight: "20px",
            }}
          >
            {comment.author.fullName}
          </a>

          {isMobile ? (
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                fontSize: 12,
                color: palette.sealBlue,
              }}
            >
              {replyTo ? (
                <>
                  {comment.author.jobTitle && comment.author.jobTitle !== "-" && (
                    <Margin mt={3}>{comment.author.jobTitle as string}</Margin>
                  )}

                  <Margin mt={5}>
                    <CommentDate date={comment.createdAt} isPrint={isPrint} />
                  </Margin>
                </>
              ) : (
                <>
                  {comment.author.jobTitle && comment.author.jobTitle !== "-" && (
                    <Margin mt={2}>{comment.author.jobTitle}</Margin>
                  )}

                  <Margin mt={5}>
                    <CommentDate date={comment.createdAt} isPrint={isPrint} />
                  </Margin>
                </>
              )}
            </div>
          ) : (
            <div
              css={{
                color: palette.sealBlue,
                fontSize: 13,
                lineHeight: 1.33,
              }}
            >
              {comment.author.__typename !== "PendingUser" &&
                comment.author.jobTitle &&
                comment.author.jobTitle !== "-" && (
                  <span>
                    {comment.author.title} <span>·</span>{" "}
                  </span>
                )}

              <span>
                <CommentDate date={comment.createdAt} isPrint={isPrint} />
              </span>
            </div>
          )}
        </div>

        <SingleReplyContextConsumer _id={comment._id}>
          {({ isReplying, toggleReply, stopReplying }) => (
            <>
              <Margin mt={15} mb={15}>
                {isEditing ? (
                  <Mutation<IEditCommentMutation, IEditCommentMutationVariables> mutation={EditComment}>
                    {(editComment) => (
                      <WriteComment
                        onSubmit={(body) => {
                          const oldComment = convertFromRaw(comment.body);
                          const newComment = convertFromRaw(body);

                          if (oldComment.getPlainText() === newComment.getPlainText()) {
                            toggleEdit();
                            return Promise.resolve();
                          }

                          return editComment({
                            optimisticResponse: {
                              __typename: "Mutation",
                              editComment: {
                                __typename: comment.__typename as any,
                                _id: comment._id,
                                body,
                              },
                            },
                            variables: {
                              commentId: comment._id,
                              body,
                            },
                          }).then(toggleEdit);
                        }}
                        initialComment={convertFromRaw(comment.body).getPlainText()}
                        actionText={intl.formatMessage({
                          defaultMessage: "Edit",
                          id: "wEQDC6",
                        })}
                      />
                    )}
                  </Mutation>
                ) : (
                  <div
                    css={{
                      fontSize: isMobile ? 13 : 14,
                    }}
                  >
                    <Editor
                      editorKey={comment._id}
                      readOnly={true}
                      editorState={EditorState.createWithContent(convertFromRaw(comment.body))}
                      onChange={() => {}}
                    />
                  </div>
                )}
              </Margin>

              {!isPrint && (
                <Mutation<IRemoveCommentMutation, IRemoveCommentMutationVariables>
                  update={(proxy) => {
                    const queryResult = proxy.readQuery({
                      query: CommentsQuery,
                      variables: {
                        targetId,
                        targetType,
                        cursor: null,
                      },
                    });

                    if (!queryResult) {
                      return;
                    }

                    const { comments } = queryResult as any;

                    proxy.writeQuery({
                      query: CommentsQuery,
                      variables: {
                        targetId,
                        targetType,
                        cursor: null,
                      },
                      data: {
                        comments: {
                          ...comments,
                          items: comments.items
                            .map((item: ICommentFieldsLegacyFragment) => {
                              if (comment.isReply) {
                                if (replyTo && item._id === replyTo._id) {
                                  return {
                                    ...item,
                                    replies: ((item.replies as any) || []).filter(
                                      (replyComment: ICommentFieldsLegacyFragment) => replyComment._id !== comment._id,
                                    ),
                                  };
                                }

                                return item;
                              } else if (comment._id === item._id) {
                                return null;
                              } else {
                                return item;
                              }
                            })
                            .filter(Boolean),
                        },
                      },
                    });
                  }}
                  mutation={RemoveComment}
                  optimisticResponse={{
                    __typename: "Mutation",
                    removeComment: true,
                  }}
                >
                  {(removeComment) => (
                    <ToggleUpvote
                      upvotesCount={comment.upvotesCount || 0}
                      viewerDidUpvote={comment.viewerDidUpvote || false}
                      targetType={IUpvoteTargetType.Comment}
                      entityType={comment.__typename as any}
                      targetId={comment._id}
                    >
                      {(toggleUpvote) => (
                        <CommentActions
                          preview={preview}
                          selfie={selfie}
                          comment={comment as any}
                          onReply={toggleReply}
                          onToggleUpvote={toggleUpvote}
                          onEdit={toggleEdit}
                          onDelete={() => {
                            removeComment({
                              variables: {
                                commentId: comment._id,
                                targetId,
                              },
                            });
                          }}
                        />
                      )}
                    </ToggleUpvote>
                  )}
                </Mutation>
              )}

              {isPhone && (
                <MobileScreen isOpen={isReplying}>
                  {() => (
                    <>
                      <MobileScreenHeader onRequestClose={toggleReply}>
                        Reply to {`${apostrophe(comment.author!.firstName)}`} comment
                      </MobileScreenHeader>

                      <MobileScreenBody>
                        <Margin mt={10}>
                          <div css={{ display: "flex", alignItems: "center" }}>
                            <ProfilePicture
                              size={38}
                              image={comment.author!.avatar}
                              name={comment.author!.fullName}
                              innovator={comment.author.__typename === "GovernmentUser" && comment.author.isInnovator}
                            />

                            <Margin ml={10}>
                              <strong css={{ fontSize: 14, fontWeight: 600 }}>{comment.author!.fullName}</strong>
                              wrote
                              <span
                                css={{
                                  fontSize: 12,
                                  display: "block",
                                  color: palette.sealBlue,
                                }}
                              >
                                <CommentDate date={comment.createdAt} isPrint={isPrint} />
                              </span>
                            </Margin>
                          </div>
                        </Margin>

                        <Margin mt={20}>
                          <div
                            css={{
                              fontSize: 14,
                              "& > span": {
                                wordBreak: "break-word",
                              },
                            }}
                          >
                            <Editor
                              editorKey={comment._id}
                              readOnly={true}
                              editorState={EditorState.createWithContent(convertFromRaw(comment.body))}
                              onChange={() => {}}
                            />
                          </div>
                        </Margin>

                        <hr
                          css={{
                            margin: 0,
                            marginTop: 20,
                            marginBottom: 20,
                            border: "none",
                            borderTop: `solid 1px ${palette.lightestGray}`,
                          }}
                        />

                        <strong
                          css={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: palette.sealBlue,
                          }}
                        >
                          <FormattedMessage defaultMessage="Your reply" id="KYK4/u" />
                        </strong>

                        <Margin mt={10}>
                          <ReplyComment targetId={targetId} targetType={targetType}>
                            {(replyComment) => (
                              <WriteComment
                                fluid={true}
                                autoFocus={true}
                                actionText={intl.formatMessage({
                                  defaultMessage: "Reply",
                                  id: "9HU8vw",
                                })}
                                placeholder={intl.formatMessage(
                                  {
                                    defaultMessage: "Write a reply to {fullName} comment",
                                    id: "6owdSR",
                                  },
                                  { fullName: apostrophe(comment.author!.fullName) },
                                )}
                                onSubmit={(body) => {
                                  stopReplying();

                                  if (comment.isReply && replyTo) {
                                    return replyComment(replyTo._id, body);
                                  } else {
                                    return replyComment(comment._id, body);
                                  }
                                }}
                              />
                            )}
                          </ReplyComment>
                        </Margin>
                      </MobileScreenBody>
                    </>
                  )}
                </MobileScreen>
              )}

              {!isPhone && isReplying && (
                <Margin mt={20}>
                  <ReplyComment targetId={targetId} targetType={targetType}>
                    {(replyComment) => (
                      <WriteComment
                        fluid={true}
                        autoFocus={true}
                        actionText={intl.formatMessage({
                          defaultMessage: "Reply",
                          id: "9HU8vw",
                        })}
                        placeholder={intl.formatMessage(
                          {
                            defaultMessage: "Write a reply to {fullName} comment",
                            id: "6owdSR",
                          },
                          { fullName: apostrophe(comment.author!.fullName) },
                        )}
                        onSubmit={(body) => {
                          stopReplying();

                          if (comment.isReply && replyTo) {
                            return replyComment(replyTo._id, body);
                          } else {
                            return replyComment(comment._id, body);
                          }
                        }}
                      />
                    )}
                  </ReplyComment>
                </Margin>
              )}

              {comment.isReply === false && (comment.replies || []).length > 0 && (
                <Margin mt={20}>
                  <div
                    css={{
                      paddingLeft: 10,
                      paddingRight: 20,
                      borderLeft: `solid 2px ${palette.sealBlue}`,
                      display: "grid",
                      gridRowGap: 20,
                    }}
                  >
                    {((comment.replies || []) as any[]).filter(notUndefinedOrNull).map((reply: IComment) => {
                      return (
                        <Comment
                          key={reply._id}
                          targetId={targetId}
                          targetType={targetType}
                          comment={reply as any}
                          replyTo={comment}
                          router={router}
                          preview={preview}
                        />
                      );
                    })}
                  </div>
                </Margin>
              )}
            </>
          )}
        </SingleReplyContextConsumer>
      </div>
    </div>
  );
}

interface ICommentDateProps {
  date: string;
  isPrint: boolean;
}

function CommentDate({ date, isPrint }: ICommentDateProps) {
  if (isPrint) {
    return <IntlRelativeTime value={date} />;
  }

  return <IntlDateTimeComment value={date} />;
}

export default withRouter(Comment);
